import {
  CSSProperties,
  ReactNode,
  TouchEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

type ApplicationResponsiveModalProps = {
  ariaLabelledBy: string
  children: ReactNode | ((closeModal: () => void) => ReactNode)
  closeAriaLabel: string
  onClose: () => void
  modalClassName: string
  closeButtonClassName?: string
  disableDismiss?: boolean
  hideCloseButtonOnMobile?: boolean
  mobileAnimationMs?: number
  swipeTarget?: 'sheet' | 'handle' | 'none'
}

const MOBILE_BREAKPOINT_QUERY = '(max-width: 575px)'
const MOBILE_CLOSE_THRESHOLD = 96

const ApplicationResponsiveModal = ({
  ariaLabelledBy,
  children,
  closeAriaLabel,
  onClose,
  modalClassName,
  closeButtonClassName,
  disableDismiss = false,
  hideCloseButtonOnMobile = false,
  mobileAnimationMs = 380,
  swipeTarget = 'sheet',
}: ApplicationResponsiveModalProps) => {
  const closeButtonRef = useRef<HTMLButtonElement | null>(null)
  const touchStartYRef = useRef<number | null>(null)
  const closingTimerRef = useRef<number | null>(null)
  const isMobileSheetRef = useRef(false)
  const [isMobileSheet, setIsMobileSheet] = useState(false)
  const [isPresented, setIsPresented] = useState(false)
  const [dragOffset, setDragOffset] = useState(0)
  const [isDragging, setIsDragging] = useState(false)

  const clearClosingTimer = useCallback(() => {
    if (closingTimerRef.current !== null) {
      window.clearTimeout(closingTimerRef.current)
      closingTimerRef.current = null
    }
  }, [])

  useEffect(() => {
    isMobileSheetRef.current = isMobileSheet
  }, [isMobileSheet])

  const handleClose = useCallback(() => {
    if (disableDismiss) {
      return
    }

    clearClosingTimer()

    if (!isMobileSheetRef.current) {
      onClose()
      return
    }

    setIsDragging(false)
    setDragOffset(0)
    setIsPresented(false)

    closingTimerRef.current = window.setTimeout(() => {
      onClose()
    }, mobileAnimationMs)
  }, [clearClosingTimer, disableDismiss, mobileAnimationMs, onClose])

  useEffect(() => {
    const mobileQuery = window.matchMedia(MOBILE_BREAKPOINT_QUERY)
    const updateMobileSheet = () => setIsMobileSheet(mobileQuery.matches)

    updateMobileSheet()
    mobileQuery.addEventListener('change', updateMobileSheet)

    const openTimer = window.requestAnimationFrame(() => {
      setIsPresented(true)
    })

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        handleClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.cancelAnimationFrame(openTimer)
      mobileQuery.removeEventListener('change', updateMobileSheet)
      clearClosingTimer()
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [clearClosingTimer, handleClose, hideCloseButtonOnMobile])

  const sheetStyle: CSSProperties & {'--sheet-translate'?: string; '--sheet-duration'?: string} =
    useMemo(() => {
      const style: CSSProperties & {
        '--sheet-translate'?: string
        '--sheet-duration'?: string
      } = {
        '--sheet-duration': `${mobileAnimationMs}ms`,
      }

      if (!isMobileSheet) {
        return style
      }

      style['--sheet-translate'] = isPresented ? `${dragOffset}px` : '100%'
      style.transition = isDragging
        ? 'none'
        : `transform ${mobileAnimationMs}ms ease, opacity ${mobileAnimationMs}ms ease`
      style.opacity = isPresented ? 1 : 0.98

      return style
    }, [dragOffset, isDragging, isMobileSheet, isPresented, mobileAnimationMs])

  const handleTouchStart = (event: TouchEvent<HTMLElement>) => {
    if (!isMobileSheet || !isPresented || disableDismiss || swipeTarget === 'none') {
      return
    }

    touchStartYRef.current = event.touches[0]?.clientY ?? null
    setIsDragging(true)
    clearClosingTimer()
  }

  const handleTouchMove = (event: TouchEvent<HTMLElement>) => {
    if (
      !isMobileSheet ||
      touchStartYRef.current === null ||
      disableDismiss ||
      swipeTarget === 'none'
    ) {
      return
    }

    const currentY = event.touches[0]?.clientY
    if (currentY === undefined) {
      return
    }

    setDragOffset(Math.max(0, currentY - touchStartYRef.current))
  }

  const handleTouchEnd = () => {
    if (
      !isMobileSheet ||
      touchStartYRef.current === null ||
      disableDismiss ||
      swipeTarget === 'none'
    ) {
      return
    }

    const shouldClose = dragOffset > MOBILE_CLOSE_THRESHOLD

    touchStartYRef.current = null
    setIsDragging(false)

    if (shouldClose) {
      handleClose()
      return
    }

    setDragOffset(0)
  }

  const swipeHandlers =
    swipeTarget === 'none'
      ? {}
      : {
          onTouchStart: handleTouchStart,
          onTouchMove: handleTouchMove,
          onTouchEnd: handleTouchEnd,
          onTouchCancel: handleTouchEnd,
        }

  return (
    <div className='application-sheet-modal-backdrop' role='presentation' onClick={handleClose}>
      <section
        className={`application-sheet-modal ${modalClassName}`}
        role='dialog'
        aria-modal='true'
        aria-labelledby={ariaLabelledBy}
        style={sheetStyle}
        onClick={(event) => event.stopPropagation()}
        {...(swipeTarget === 'sheet' ? swipeHandlers : {})}
      >
        <div
          className='application-sheet-modal-handle'
          aria-hidden='true'
          {...(swipeTarget === 'handle' ? swipeHandlers : {})}
        />

        {!disableDismiss && (
          <button
            type='button'
            className={`application-sheet-modal-close ${
              hideCloseButtonOnMobile ? 'application-sheet-modal-close-mobile-hidden' : ''
            } ${closeButtonClassName ?? ''}`.trim()}
            onClick={handleClose}
            ref={closeButtonRef}
            aria-label={closeAriaLabel}
          >
            <i className='bi bi-x-lg' aria-hidden='true' />
          </button>
        )}

        {typeof children === 'function' ? children(handleClose) : children}
      </section>
    </div>
  )
}

export default ApplicationResponsiveModal
